'use client';

import { Log, UserManager, WebStorageStateStore } from 'oidc-client-ts';

declare global {
    var __OIDC_USER_MANAGER__: UserManager | undefined;
    var __OIDC_LOG_INIT__: boolean | undefined;
}

function createUserManager(): UserManager {
    if (typeof window === 'undefined') {
        throw new Error('UserManager must only be used in the browser');
    }

    if (!globalThis.__OIDC_LOG_INIT__) {
        Log.setLogger(console);
        Log.setLevel(
            process.env.NEXT_PUBLIC_ENV === 'localhost' && process.env.NEXT_PUBLIC_DEBUG === 'true'
                ? Log.DEBUG
                : Log.NONE,
        );
        globalThis.__OIDC_LOG_INIT__ = true;
    }

    const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY as string;
    const authorizationEndpoint = process.env.NEXT_PUBLIC_OIDC_AUTHORIZATION_ENDPOINT;
    const tokenEndpoint = process.env.NEXT_PUBLIC_OIDC_TOKEN_ENDPOINT;
    const userinfoEndpoint = process.env.NEXT_PUBLIC_OIDC_USERINFO_ENDPOINT;
    const jwksUri = process.env.NEXT_PUBLIC_OIDC_JWKS_URI;
    const endSessionEndpoint = process.env.NEXT_PUBLIC_OIDC_END_SESSION_ENDPOINT;
    const revocationEndpoint = process.env.NEXT_PUBLIC_OIDC_REVOCATION_ENDPOINT;

    const hasStaticMetadata =
        Boolean(authorizationEndpoint) &&
        Boolean(tokenEndpoint) &&
        Boolean(userinfoEndpoint) &&
        Boolean(jwksUri) &&
        Boolean(endSessionEndpoint);

    const mgr = new UserManager({
        authority: authority,
        client_id: process.env.NEXT_PUBLIC_OIDC_CLIENT_ID as string,
        redirect_uri: process.env.NEXT_PUBLIC_OIDC_REDIRECT_URI as string,
        response_type: 'code',
        scope: 'openid profile email',
        post_logout_redirect_uri: process.env.NEXT_PUBLIC_OIDC_POST_LOGOUT_URI as string,
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        stateStore: new WebStorageStateStore({ store: window.localStorage }),
        automaticSilentRenew: true,
        silent_redirect_uri: process.env.NEXT_PUBLIC_OIDC_SILENT_REDIRECT_URI as string,
        metadata: hasStaticMetadata
            ? {
                  issuer: authority,
                  authorization_endpoint: authorizationEndpoint as string,
                  token_endpoint: tokenEndpoint as string,
                  userinfo_endpoint: userinfoEndpoint as string,
                  jwks_uri: jwksUri as string,
                  end_session_endpoint: endSessionEndpoint as string,
                  revocation_endpoint: revocationEndpoint || undefined,
              }
            : undefined,
    });

    return mgr;
}

export function getUserManager(): UserManager {
    if (globalThis.__OIDC_USER_MANAGER__) {
        return globalThis.__OIDC_USER_MANAGER__;
    }
    const mgr = createUserManager();
    globalThis.__OIDC_USER_MANAGER__ = mgr;
    return mgr;
}
